import PropTypes from 'prop-types';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import ReactGA from 'react-ga4';
import { connect } from 'react-redux';

import { updateProvisionalOfferRequest } from '../../actions/updateProvisionalOfferActions';
import { redirectToSignIn } from '../../api/OryClient';
import Loading from '../../components/Loading';
import {
  PROVISIONAL_OFFER_VIEW,
  PROVISIONAL_OFFER_ACCEPT,
  PROVISIONAL_OFFER_DECLINE,
} from '../../constants/heapEvents';
import analytics from '../../lib/analytics';
import { useAuth } from '../../lib/authContext';
import { track, optionsShown } from '../../lib/heapHelpers';
import useProvisionalOffer from '../../lib/hooks/useProvisionalOffer';
import useScriptInjection from '../../lib/hooks/useScriptInjection';
import {
  updateProvisionalOfferSelector,
  updateProvisionalOfferLoadingSelector,
} from '../../reducers/selectors';
import ProvisionalOfferOptionsPage from '../ProvisionalOfferOptionsPage/ProvisionalOfferOptionsPage';

import './index.scss';

const ProvisionalOfferPage = ({
  applicationId,
  dispatchUpdateProvisionalOffer,
  updateProvisionalOfferResponse,
  updateProvisionalOfferIsLoading,
}) => {
  const { data, isLoading } = useProvisionalOffer(applicationId);
  const { REACT_APP_UPDATE_PLATFORM_REDIRECT: PLATFORM_UPDATE_REDIRECT } = process.env;
  const redirectUrl = url => `${PLATFORM_UPDATE_REDIRECT}`.replace('{platformUrl}', url);

  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isProvisionalOfferAccepted, setIsProvisionalOfferAccepted] = useState(false);
  const [isStatusInProvisionalOffer, setIsStatusInProvisionalOffer] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cosignerDetails, setCosignerDetails] = useState({});
  const handleSelectedOffer = offer => setSelectedOffer(offer);
  const handleCosignerDetails = details => {
    setCosignerDetails({ ...cosignerDetails, ...details });
  };

  useEffect(() => {
    if (Object.keys(data).length !== 0 && data.borrower_status === 'Provisional Offer') {
      setIsStatusInProvisionalOffer(true);
    } else if (
      Object.keys(data).length !== 0 &&
      (data.borrower_status === 'Loan Approved' ||
        data.borrower_status === 'Sign Loan Agreement' ||
        data.borrower_status === 'Disbursed' ||
        data.borrower_status === 'Verification')
    ) {
      setIsProvisionalOfferAccepted(true);
    } else {
      setIsStatusInProvisionalOffer(false);
      setIsProvisionalOfferAccepted(false);
    }
  }, [data]);

  useEffect(() => {
    if (
      Object.keys(data).length !== 0 &&
      data.borrower_status !== 'Provisional Offer' &&
      data.borrower_status !== 'Loan Approved' &&
      data.borrower_status !== 'Sign Loan Agreement' &&
      data.borrower_status !== 'Disbursed' &&
      data.borrower_status !== 'Verification'
    ) {
      window.location.href = `${process.env.REACT_APP_PLATFORM_DASHBOARD}`;
    } else if (Object.keys(updateProvisionalOfferResponse).length !== 0) {
      window.location.href = redirectUrl(updateProvisionalOfferResponse.redirect_url);
    }
  }, [data, isLoading, updateProvisionalOfferResponse, updateProvisionalOfferIsLoading]);

  useEffect(() => {
    if (applicationId && data.customer_guid) {
      track(PROVISIONAL_OFFER_VIEW, {
        'Application GUID': applicationId,
        'Customer GUID': data.customer_guid,
        'Options shown': optionsShown(data.offer_options),
      });
    }
  }, [
    applicationId,
    data.customer_guid,
    data.offer_options,
    data.step_down_offer_options,
    data.first_disbursement_date,
    data.first_repayment_date,
  ]);

  useEffect(() => analytics.sendCustomPixelEvent('AppSubmitted'), []);

  const acceptOffer = () => {
    setIsProcessing(true);
    analytics.sendEvent('Click - Accept', 'provisional-offer');

    const acceptedOption = selectedOffer?.cosignerName ? 'Cosigner option' : 'Standard option';

    track(PROVISIONAL_OFFER_ACCEPT, {
      'Accepted Option': acceptedOption,
      'Application GUID': applicationId,
      'Customer GUID': data.customer_guid,
      'Options shown': optionsShown(data.offer_options),
    });
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-11084578152/_P-tCNSSxpkYEOj6xKUp',
      });
    }
    dispatchUpdateProvisionalOffer({
      applicationId: applicationId,
      accepted: true,
      term: selectedOffer.term_in_months,
      offerGuid: selectedOffer.offer_guid,
      cosignerName: cosignerDetails.cosignerName,
      cosignerEmail: cosignerDetails.cosignerEmail,
    });
  };

  const rejectOffer = () => {
    setIsProcessing(true);
    analytics.sendEvent('Click - Decline', 'provisional-offer');

    track(PROVISIONAL_OFFER_DECLINE, {
      'Application GUID': applicationId,
      'Customer GUID': data.customer_guid,
      'Options shown': optionsShown(data.offer_options),
    });
    dispatchUpdateProvisionalOffer({
      applicationId: applicationId,
      accepted: false,
    });
  };

  // Study portal partner

  const trackingVariables = `sp_convtype = 's';`;
  ReactGA.gtag('config', 'AW-11084578152');
  useScriptInjection(null, trackingVariables, null, 'text/javascript');
  useScriptInjection('https://studyportalstracking.com/js/spconv.js', null, true, null);

  const renderProvisionalOfferOptionsPage = () => {
    return (
      <ProvisionalOfferOptionsPage
        data={data}
        isProvisionalOfferAccepted={isProvisionalOfferAccepted}
        acceptOffer={acceptOffer}
        rejectOffer={rejectOffer}
        selectedOffer={selectedOffer}
        onSelectedOfferChange={handleSelectedOffer}
        handleCosignerDetails={handleCosignerDetails}
      />
    );
  };

  const { isAuthenticated, isAuthenticating } = useAuth();

  useLayoutEffect(() => {
    if (!isAuthenticating && !isAuthenticated) redirectToSignIn();
  }, [isAuthenticated, isAuthenticating]);

  const shouldShowLoading = () => {
    return (
      isAuthenticating ||
      updateProvisionalOfferIsLoading ||
      isLoading ||
      isProcessing ||
      (!isProvisionalOfferAccepted && !isStatusInProvisionalOffer && !isLoading)
    );
  };

  return (
    <div>
      {shouldShowLoading() ? (
        <div className="po-centered-column">
          <Loading /> <p>Please wait...</p>
        </div>
      ) : (
        <div>{renderProvisionalOfferOptionsPage(data)}</div>
      )}
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    applicationId: props.match.params.applicationId,
    updateProvisionalOfferResponse: updateProvisionalOfferSelector(state),
    updateProvisionalOfferIsLoading: updateProvisionalOfferLoadingSelector(state),
  };
};

const mapDispatchToProps = {
  dispatchUpdateProvisionalOffer: updateProvisionalOfferRequest,
};

ProvisionalOfferPage.defaultProps = {
  updateProvisionalOfferIsLoading: false,
};

ProvisionalOfferPage.propTypes = {
  applicationId: PropTypes.string.isRequired,
  dispatchUpdateProvisionalOffer: PropTypes.func.isRequired,
  updateProvisionalOfferResponse: PropTypes.shape({
    redirect_url: PropTypes.string.isRequired,
  }).isRequired,
  updateProvisionalOfferIsLoading: PropTypes.bool,
};

export { ProvisionalOfferPage, mapStateToProps, mapDispatchToProps };
export default connect(mapStateToProps, mapDispatchToProps)(ProvisionalOfferPage);
